import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ClipboardList, FolderKanban, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { TASK_STATUS } from "@/lib/roles";
import TaskDetailDialog, { DueBadge } from "@/components/volunteer/TaskDetailDialog";

const selectCls = "rounded-lg border border-[#e4d8c4] bg-white px-2.5 py-1.5 text-xs font-semibold text-[#00334a]";

export default function MyWork() {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(() => {
    api.get("/tasks?mine=true").then(({ data }) => setTasks(data)).catch(() => {});
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/tasks/${id}/status`, { status });
      toast.success("Task updated");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const myProjects = projects.filter((p) => p.members?.some((m) => m));

  return (
    <div className="space-y-6" data-testid="volunteer-mywork">
      <div>
        <span className="hf-eyebrow">Your workspace</span>
        <h1 className="mt-1 font-heading text-3xl font-semibold text-[#00334a]">My Work</h1>
      </div>

      <div>
        <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-[#00334a]">
          <ClipboardList className="h-5 w-5 text-[#c97049]" /> My Tasks
        </h2>
        {tasks.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#e4d8c4] bg-white p-8 text-center text-[#7c7a6a]">
            No tasks assigned yet. Check the Up for Grabs board to claim open work.
          </div>
        ) : (
          <div className="space-y-2">
            {tasks.map((t) => (
              <div key={t.id} className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#e4d8c4] bg-white p-4" data-testid={`mytask-${t.id}`}>
                <button onClick={() => setDetailId(t.id)} className="min-w-0 flex-1 text-left" data-testid={`mytask-open-${t.id}`}>
                  <div className="font-semibold text-[#00334a] hover:text-[#c97049]">{t.title}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-[#7c7a6a]">
                    <span>{t.project_name}{t.due_date ? ` · due ${t.due_date}` : ""}</span>
                    <DueBadge task={t} />
                  </div>
                </button>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${TASK_STATUS[t.status]?.cls}`}>{TASK_STATUS[t.status]?.label}</span>
                <select value={t.status} onChange={(e) => setStatus(t.id, e.target.value)} className={selectCls} data-testid={`mytask-status-${t.id}`}>
                  <option value="open">Open</option>
                  <option value="in_progress">In progress</option>
                  <option value="done">Done</option>
                </select>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-[#00334a]">
          <FolderKanban className="h-5 w-5 text-[#65949c]" /> My Projects
        </h2>
        {myProjects.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#e4d8c4] bg-white p-8 text-center text-[#7c7a6a]">
            You're not a member of any project yet.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {myProjects.map((p) => (
              <div key={p.id} className="rounded-2xl border border-[#e4d8c4] bg-white p-4" data-testid={`myproject-${p.id}`}>
                <div className="font-heading text-lg font-semibold text-[#00334a]">{p.name}</div>
                <p className="mt-1 text-sm text-[#7c7a6a]">{p.description}</p>
                <div className="mt-2 flex items-center gap-3 text-xs text-[#7c7a6a]">
                  <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> {p.open_task_count} open of {p.task_count} tasks</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <TaskDetailDialog taskId={detailId} open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)} onChanged={load} />
    </div>
  );
}
